<template>
	<v-container class="resource-configuration">
		<form-section
			icon="format_list_numbered"
			eyebrow="Orden"
			title="Posición"
			description="Define el lugar que ocupa dentro del menú."
			tight
		>
			<v-text-field
				type="number"
				solo
				hide-details
				v-if="resource.templateLanguages"
				v-model="resource.templateLanguages.position"
				@input="itWasUpdated"
			/>
			<v-text-field
				type="number"
				solo
				hide-details
				v-else
				v-model="newPosition"
			/>
		</form-section>
		<form-section
			icon="translate"
			eyebrow="Idiomas"
			title="Nombre"
			description="Puedes usar el nombre recomendado o escribir uno diferente."
			:badge="namesBadge"
		>
			<template slot="aside">
				<button
					v-if="hasRecommendedName"
					type="button"
					class="recommended-name-button"
					@click="useRecommendedName"
				>
					<v-icon>lightbulb_outline</v-icon>
					Usar “{{ recommendedName }}”
				</button>
			</template>
			<div
				class="name-row"
				v-for="(lang, indexLang) in resource.templateLanguagesArray"
				:key="indexLang"
			>
				<span class="name-row__code">{{ lang.id }}</span>
				<v-text-field
					solo
					hide-details
					placeholder="Nombre que verá el usuario"
					v-model="lang.name"
					@input="itWasUpdated"
				/>
			</div>
		</form-section>
		<form-section
			icon="image"
			eyebrow="Identidad"
			title="Ícono"
			:description="requireIcon ?
				'Se muestra junto al nombre en el menú.' :
				'Opcional: si no cargas uno, el menú usa el ícono por defecto.'"
			tone="info"
		>
			<v-layout wrap align-center class="icon-row">
				<div class="resource-icon-preview" :title="resource.pathIcon || 'Sin ícono'">
					<img
						v-if="showIconPreview"
						:src="resource.pathIcon"
						alt="Ícono del recurso"
						@error="markIconAsBroken"
					>
					<v-icon v-else>broken_image</v-icon>
				</div>
				<upload-image
					label-ref="up"
					label-for="image"
					input-id="image"
					@send-image="getImage"
				/>
				<v-flex class="icon-path-field">
					<v-text-field
						solo
						hide-details
						v-model="resource.pathIcon"
						placeholder="URL del ícono"
						@input="itWasUpdated"
					/>
				</v-flex>
			</v-layout>
		</form-section>
		<section class="modal-filter-footer">
			<modal-sidebar-button
				:is-loading="$v.$invalid"
				:helper-text="footerHelper"
				@accept-button-clicked="$emit('save-data', resource, newPosition)"
				@cancel-button-clicked="$emit('close-modal')"
			/>
		</section>
	</v-container>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import uploadImage from '@/components/shared/upload-image';

function created() {
	this.codeProject = this.$route.params.codeProject;
	const currentPosition = Number(this.resource.position ||
		(this.resource.templateLanguages && this.resource.templateLanguages.position));
	const automaticPosition = currentPosition || this.suggestedPosition;
	this.resource.position = automaticPosition;
	if (this.resource.templateLanguages) {
		if (!this.resource.templateLanguages.position) {
			this.$set(this.resource.templateLanguages, 'position', automaticPosition);
		}
	} else {
		this.newPosition = automaticPosition;
	}
}

function getImage(img) {
	this.iconBroken = false;
	this.resource.pathIcon = img.urlImage;
	this.$emit('set-image', img);
}

function markIconAsBroken() {
	this.iconBroken = true;
}

function showIconPreview() {
	return Boolean(this.resource.pathIcon) && !this.iconBroken;
}

function watchPathIcon() {
	this.iconBroken = false;
}

function languageNames() {
	return this.resource.templateLanguagesArray || [];
}

function namesBadge() {
	const languages = this.languageNames;
	const complete = languages.filter(language => String(language.name || '').trim()).length;
	return languages.length ? `${complete}/${languages.length} listos` : '';
}

function footerHelper() {
	if (!this.$v.$invalid) return 'Todo listo para guardar.';
	const pending = this.languageNames
		.filter(language => !String(language.name || '').trim())
		.map(language => language.id);
	if (pending.length) return `Falta el nombre en: ${pending.join(', ')}.`;
	return this.requireIcon ? 'Falta cargar el ícono.' : 'Completa los datos requeridos.';
}

function itWasUpdated() {
	this.$emit('item-edited');
}

function recommendedName() {
	return this.resource.name || this.resource.title || '';
}

function hasRecommendedName() {
	return Boolean(this.recommendedName);
}

function useRecommendedName() {
	if (!this.hasRecommendedName) return;
	(this.resource.templateLanguagesArray || []).forEach((language) => {
		if (!language.name) {
			this.$set(language, 'name', this.recommendedName);
		}
	});
	this.itWasUpdated();
}

function validations() {
	return {
		resource: {
			pathIcon: this.requireIcon ? { required } : {},
			position: { required },
			templateLanguagesArray: {
				$each: {
					name: { required },
				},
			},
		},
	};
}

function data() {
	return {
		codeProject: '',
		iconBroken: false,
		position: 0,
		newPosition: null,
	};
}

export default {
	name: 'resource-configuration',
	components: {
		uploadImage,
	},
	created,
	computed: {
		footerHelper,
		hasRecommendedName,
		languageNames,
		namesBadge,
		recommendedName,
		showIconPreview,
	},
	data,
	methods: {
		getImage,
		itWasUpdated,
		markIconAsBroken,
		useRecommendedName,
	},
	props: {
		requireIcon: {
			default: true,
			type: Boolean,
		},
		resource: {
			default: () => {},
			type: Object,
		},
		suggestedPosition: {
			default: 1,
			type: Number,
		},
	},
	validations,
	watch: {
		'resource.pathIcon': watchPathIcon,
	},
};
</script>

<style lang="scss" scoped>
.resource-configuration {
	padding: 16px 14px 20px;
}

.name-row {
	align-items: center;
	display: flex;
	gap: 10px;
	margin-bottom: 9px;

	&:last-child { margin-bottom: 0; }
}

.name-row__code {
	background: #1a4338;
	border-radius: 7px;
	color: #fff;
	flex: 0 0 auto;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: .08em;
	padding: 4px 9px;
	text-transform: uppercase;
	width: 44px;
	text-align: center;
}

.icon-row { gap: 10px; }

.icon-path-field { min-width: 160px; }

.resource-icon-preview {
	align-items: center;
	background: linear-gradient(135deg, #2b435b 0%, #3f6187 100%);
	border-radius: 10px;
	box-shadow: 0 3px 8px rgba(43, 67, 91, .22);
	display: inline-flex;
	flex: 0 0 auto;
	height: 48px;
	justify-content: center;
	overflow: hidden;
	width: 48px;

	img { height: 30px; object-fit: contain; width: 30px; }
	.icon { color: rgba(255, 255, 255, .55); font-size: 22px; }
}

.recommended-name-button {
	align-items: center;
	background: #eaf4fe;
	border: 1px solid #cce2f6;
	border-radius: 7px;
	color: #176fc1;
	cursor: pointer;
	display: flex;
	flex: 0 0 auto;
	font-size: 10px;
	font-weight: 600;
	height: 30px;
	outline: none;
	padding: 0 10px;
	white-space: nowrap;

	.icon { color: #e19a18; font-size: 17px; margin-right: 5px; }
	&:hover { background: #dceefe; }
}

@media (max-width: 520px) {
	.resource-configuration { padding: 12px 10px 16px; }
	.name-row { align-items: stretch; flex-direction: column; gap: 5px; }
	.name-row__code { align-self: flex-start; }
}
</style>



// WEBPACK FOOTER //
// src/components/resources/resource-configuration.vue